import React from 'react';

import { ApolloCache, isReference, Reference, useApolloClient } from '@apollo/client';
import { Badge } from '@oysterjs/ui/badge';
import { Button } from '@oysterjs/ui/button';
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogDescription,
  DialogTitle
} from '@oysterjs/ui/dialog';
import { Description, Label } from '@oysterjs/ui/fieldset';
import { Subheading } from '@oysterjs/ui/heading';
import { Radio, RadioField, RadioGroup } from '@oysterjs/ui/radio';
import { Spinner } from '@oysterjs/ui/spinner';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@oysterjs/ui/table';
import { Text } from '@oysterjs/ui/text';

import { messageFromError, messageOfFailure } from '@oysterjs/core/graphql/errors';

import { getClientMergeSide } from '~/api/data/clients/queries';
import { useMergeManyClientsMutation } from '~/api/graphql';

import { ClientLinesBadges, countLine, OrDash } from '~/components/clients/clients-columns';
import { dateFormatter } from '~/components/table-formatters';
import {
  compareFields,
  droppedValues,
  FieldPicks,
  FieldRow,
  fieldsInputOf,
  MergeClient,
  MergeFieldKey,
  mergeOrder
} from './client-merge-fields';
import { FormError } from '~/components/form-error';

const MERGE_FAILED = 'That merge did not go through. Nothing was changed.';
const LOAD_FAILED = 'Could not load these clients.';

/** The survivor plus the nine clients the server will absorb into it at once. */
export const MAX_CLIENTS_PER_MERGE = 10;

export type ClientMergeGroup = { clientIds: string[] };

/** The `clients` connection as it sits in the cache, with nodes as references. */
type ClientsConnectionRef = { edges: { node: Reference }[]; totalCount: number };

/**
 * Nothing in the merge response names the absorbed rows, so they are dropped by
 * hand. A refetch would discard every page loaded past the first.
 */
const forgetAbsorbedClients = (cache: ApolloCache<object>, absorbedIds: string[]) => {
  const absorbed = new Set(absorbedIds);
  cache.modify({
    fields: {
      clients: (existing: Reference | ClientsConnectionRef, { readField }) => {
        if (isReference(existing)) return existing;
        const edges = existing.edges.filter(
          (edge) => !absorbed.has(readField<string>('id', edge.node) ?? '')
        );
        const dropped = existing.edges.length - edges.length;
        if (dropped === 0) return existing;
        return { ...existing, edges, totalCount: existing.totalCount - dropped };
      }
    }
  });
};

const namesOf = (ordered: MergeClient[], ids: string[]) =>
  ordered
    .filter((client) => ids.includes(client.id))
    .map((client) => client.name)
    .join(', ');

/** Which record the others are merged into. */
const SurvivorChoice = (props: {
  clients: MergeClient[];
  survivorId: string;
  onChange: (id: string) => void;
}) => (
  <RadioGroup
    value={props.survivorId}
    onChange={props.onChange}
    aria-label="Merge into"
    className="!space-y-0 divide-y divide-neutral-200 overflow-hidden rounded-lg border border-neutral-200 dark:divide-neutral-700 dark:border-neutral-700"
  >
    {props.clients.map((client) => {
      const survives = client.id === props.survivorId;
      return (
        <RadioField
          key={client.id}
          className={survives ? 'bg-sky-50 px-4 py-3 dark:bg-sky-500/10' : 'px-4 py-3'}
        >
          <Radio value={client.id} color="sky" />
          <Label className="flex flex-wrap items-center justify-between gap-2">
            <span className="font-semibold">{client.name}</span>
            {survives ? (
              <Badge color="sky">Merging into</Badge>
            ) : (
              <Badge color="zinc">Will be merged</Badge>
            )}
          </Label>
          <Description>
            {countLine(client)} · Added {dateFormatter.format(new Date(client.createdAt))}
          </Description>
        </RadioField>
      );
    })}
  </RadioGroup>
);

/** Where a value came from, when that is not simply the record merged into. */
const SourceNote = (props: { ordered: MergeClient[]; sourceId: string | null }) => {
  const survivor = props.ordered[0];
  if (!props.sourceId || props.sourceId === survivor.id) {
    return null;
  }
  return (
    <span className="block text-xs text-neutral-500 dark:text-neutral-400">
      from {namesOf(props.ordered, [props.sourceId])}
    </span>
  );
};

/**
 * The record the merge will leave, field by field. A field the clients disagree
 * on lists every distinct value to choose from; every other field only says
 * where its value comes from.
 */
const MergedRecord = (props: {
  ordered: MergeClient[];
  rows: FieldRow[];
  onPick: (field: MergeFieldKey, clientId: string) => void;
}) => {
  const { ordered } = props;
  const survivor = ordered[0];

  return (
    <Table dense className="[--gutter:0px]">
      <TableHead>
        <TableRow>
          <TableHeader className="w-24 text-xs text-neutral-500 dark:text-neutral-400">
            Field
          </TableHeader>
          <TableHeader className="text-xs text-neutral-500 dark:text-neutral-400">
            Merged record
          </TableHeader>
        </TableRow>
      </TableHead>
      <TableBody>
        <TableRow>
          <TableCell className="font-medium">Name</TableCell>
          <TableCell className="whitespace-normal">{survivor.name}</TableCell>
        </TableRow>
        {props.rows.map((row) => (
          <TableRow
            key={row.key}
            className={row.conflict ? 'bg-amber-50/60 dark:bg-amber-500/10' : undefined}
          >
            <TableCell className="align-top font-medium">{row.label}</TableCell>
            <TableCell className="whitespace-normal">
              {row.conflict ? (
                <RadioGroup
                  value={
                    row.options.find((o) => o.clientIds.includes(row.sourceId ?? ''))?.clientIds[0]
                  }
                  onChange={(clientId: string) => props.onPick(row.key, clientId)}
                  aria-label={`${row.label} to keep`}
                  className="!space-y-2"
                >
                  {row.options.map((option) => (
                    <RadioField key={option.clientIds[0]} className="!gap-x-2">
                      <Radio value={option.clientIds[0]} color="sky" />
                      <Label>{option.value.text}</Label>
                      <Description>from {namesOf(ordered, option.clientIds)}</Description>
                    </RadioField>
                  ))}
                </RadioGroup>
              ) : (
                <>
                  <OrDash value={row.merged?.text} />
                  <SourceNote ordered={ordered} sourceId={row.sourceId} />
                </>
              )}
            </TableCell>
          </TableRow>
        ))}
        <TableRow>
          <TableCell className="font-medium">Lines</TableCell>
          <TableCell>
            <ClientLinesBadges
              isCommercial={ordered.some((c) => c.isCommercial)}
              isPersonal={ordered.some((c) => c.isPersonal)}
            />
          </TableCell>
        </TableRow>
      </TableBody>
    </Table>
  );
};

/**
 * Names what the merge will do before it does it, because there is no undo:
 * the record left at the end, then what each client brings and gives up.
 */
const Confirmation = (props: { ordered: MergeClient[]; rows: FieldRow[] }) => {
  const [survivor, ...absorbed] = props.ordered;
  return (
    <div className="space-y-4 text-sm text-neutral-700 dark:text-neutral-300">
      <p>
        <strong>{absorbed.length === 1 ? absorbed[0].name : `${absorbed.length} clients`}</strong>{' '}
        will be merged into <strong>{survivor.name}</strong>. This cannot be undone.
      </p>

      <dl className="grid grid-cols-[6rem_1fr] gap-x-3 gap-y-1.5 rounded-lg bg-neutral-50 p-4 dark:bg-neutral-900">
        <dt className="text-neutral-500 dark:text-neutral-400">Name</dt>
        <dd className="font-medium text-neutral-900 dark:text-neutral-100">{survivor.name}</dd>
        {props.rows.map((row) => (
          <React.Fragment key={row.key}>
            <dt className="text-neutral-500 dark:text-neutral-400">{row.label}</dt>
            <dd className="text-neutral-900 dark:text-neutral-100">
              <OrDash value={row.merged?.text} />
            </dd>
          </React.Fragment>
        ))}
        <dt className="text-neutral-500 dark:text-neutral-400">Lines</dt>
        <dd>
          <ClientLinesBadges
            isCommercial={props.ordered.some((c) => c.isCommercial)}
            isPersonal={props.ordered.some((c) => c.isPersonal)}
          />
        </dd>
      </dl>

      <ul className="divide-y divide-neutral-200 rounded-lg border border-neutral-200 dark:divide-neutral-700 dark:border-neutral-700">
        {props.ordered.map((client, index) => {
          const dropped = droppedValues(client, props.rows);
          return (
            <li key={client.id} className="space-y-1 px-4 py-3">
              <div className="flex items-center justify-between gap-2">
                <span className="font-semibold text-neutral-900 dark:text-neutral-100">
                  {client.name}
                </span>
                <span className="text-neutral-500 dark:text-neutral-400">
                  {index === 0 ? 'Has' : 'Brings'} {countLine(client)}
                </span>
              </div>
              {dropped.length > 0 && (
                <p className="text-neutral-500 dark:text-neutral-400">
                  <span>{index === 0 ? 'Replaced' : 'Not kept'}</span>:{' '}
                  {dropped.map((value, i) => (
                    <React.Fragment key={value.label}>
                      {i > 0 && ', '}
                      <span className="line-through">{value.text}</span>
                    </React.Fragment>
                  ))}
                </p>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
};

type LoadState =
  | { status: 'loading' }
  | { status: 'failed'; message: string }
  | { status: 'loaded'; clients: MergeClient[] | null };

/**
 * Every record fresh rather than from cache: the list they came from may be
 * minutes old, and a stale phone number here is a wrong choice made
 * confidently. Null clients when any one is gone.
 */
const useMergeClients = (ids: string[]): { state: LoadState; retry: () => void } => {
  const apollo = useApolloClient();
  const [state, setState] = React.useState<LoadState>({ status: 'loading' });
  const [attempt, setAttempt] = React.useState(0);
  const key = ids.join(',');

  React.useEffect(() => {
    let cancelled = false;
    setState({ status: 'loading' });
    Promise.all(key.split(',').map((id) => getClientMergeSide(id, apollo)))
      .then((results) => {
        if (cancelled) return;
        // The app's client resolves a failed query with its errors rather than
        // throwing, and its data is then null for a reason other than a gone record.
        const failure = results.find((result) => result.error);
        if (failure) {
          setState({
            status: 'failed',
            message: messageFromError(failure.error, LOAD_FAILED)
          });
          return;
        }
        const clients = results.map((result) => result.data);
        setState({
          status: 'loaded',
          clients: clients.every((c) => c !== null) ? (clients as MergeClient[]) : null
        });
      })
      .catch((err: unknown) => {
        if (!cancelled) setState({ status: 'failed', message: messageFromError(err, LOAD_FAILED) });
      });
    return () => {
      cancelled = true;
    };
  }, [apollo, key, attempt]);

  return { state, retry: () => setAttempt((n) => n + 1) };
};

const MergeBody = (props: {
  group: ClientMergeGroup;
  onClose: () => void;
  onMerged: (survivor: MergeClient, absorbedIds: string[]) => void;
}) => {
  const { group } = props;
  const apollo = useApolloClient();
  const { state, retry } = useMergeClients(group.clientIds);
  const [mergeManyClients, { loading: merging }] = useMergeManyClientsMutation();

  const [survivorId, setSurvivorId] = React.useState(group.clientIds[0]);
  // Picks name a client rather than a side, so they keep their meaning when the
  // survivor changes.
  const [picks, setPicks] = React.useState<FieldPicks>({});
  const [confirming, setConfirming] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  if (state.status === 'loading') {
    return (
      <DialogBody>
        <div className="flex justify-center py-12">
          <Spinner />
        </div>
      </DialogBody>
    );
  }

  if (state.status === 'failed') {
    return (
      <>
        <DialogBody>
          <FormError message={state.message} />
        </DialogBody>
        <DialogActions>
          <Button plain onClick={props.onClose}>
            Close
          </Button>
          <Button color="sky" onClick={retry}>
            Try again
          </Button>
        </DialogActions>
      </>
    );
  }

  if (!state.clients) {
    return (
      <>
        <DialogBody>
          <Text>
            One of these clients is no longer available. It may have been merged or deleted since
            the list was loaded.
          </Text>
        </DialogBody>
        <DialogActions>
          <Button plain onClick={props.onClose}>
            Close
          </Button>
        </DialogActions>
      </>
    );
  }

  const ordered = mergeOrder(state.clients, survivorId);
  const [survivor, ...absorbed] = ordered;
  const rows = compareFields(ordered, picks);

  const confirm = async () => {
    setError(null);
    const absorbedIds = absorbed.map((client) => client.id);
    const fail = (message: string) => {
      setError(message);
      setConfirming(false);
    };
    try {
      const result = await mergeManyClients({
        variables: {
          input: {
            survivorClientId: survivor.id,
            absorbedClientIds: absorbedIds,
            fields: fieldsInputOf(rows, picks)
          }
        }
      });
      const message = messageOfFailure(result, MERGE_FAILED);
      if (message) {
        fail(message);
        return;
      }
      if (result.data?.mergeManyClients) {
        forgetAbsorbedClients(apollo.cache, absorbedIds);
        // Not pruned: the server repoints absorbed clients' pairs onto the survivor.
        void apollo.refetchQueries({
          include: ['ClientMergeCandidateCount', 'ClientMergeCandidates']
        });
        props.onMerged(result.data.mergeManyClients, absorbedIds);
      }
    } catch (err) {
      fail(messageFromError(err, MERGE_FAILED));
    }
  };

  if (confirming) {
    return (
      <>
        <DialogBody>
          <Confirmation ordered={ordered} rows={rows} />
          {error && <FormError message={error} />}
        </DialogBody>
        <DialogActions>
          <Button plain disabled={merging} onClick={() => setConfirming(false)}>
            Back
          </Button>
          <Button color="sky" disabled={merging} onClick={confirm}>
            {merging ? 'Merging...' : 'Confirm merge'}
          </Button>
        </DialogActions>
      </>
    );
  }

  const conflicts = rows.filter((row) => row.conflict).length;

  return (
    <>
      <DialogBody className="space-y-6">
        <section className="space-y-2">
          <Subheading level={3}>Merge into</Subheading>
          <Text>
            The other {absorbed.length === 1 ? 'client is' : `${absorbed.length} clients are`}{' '}
            merged into the one you choose, which keeps its name.
          </Text>
          <SurvivorChoice
            clients={state.clients}
            survivorId={survivorId}
            onChange={setSurvivorId}
          />
        </section>

        <section className="space-y-2">
          <Subheading level={3}>Merged record</Subheading>
          <Text>
            {conflicts === 0
              ? 'The records agree wherever more than one has a value, so there is nothing else to choose.'
              : `The records disagree on ${conflicts === 1 ? 'one field' : `${conflicts} fields`}. Pick the value to keep for each.`}
          </Text>
          <MergedRecord
            ordered={ordered}
            rows={rows}
            onPick={(field, clientId) => setPicks((prev) => ({ ...prev, [field]: clientId }))}
          />
        </section>
        {error && <FormError message={error} />}
      </DialogBody>
      <DialogActions>
        <Button plain onClick={props.onClose}>
          Cancel
        </Button>
        <Button color="sky" onClick={() => setConfirming(true)}>
          Review merge
        </Button>
      </DialogActions>
    </>
  );
};

/**
 * Merges two or more clients into one: pick the record the rest merge into,
 * settle each field they disagree on, then confirm against a client-by-client
 * account of what moves. Opened from the Clients list and from the duplicates
 * queue, which is why the group arrives as ids and is read afresh here rather
 * than as whatever fragment the caller had.
 */
export const ClientMergeDialog = (props: {
  group: ClientMergeGroup | null;
  onClose: () => void;
  onMerged: (survivor: MergeClient, absorbedIds: string[]) => void;
}) => (
  <Dialog open={props.group !== null} onClose={props.onClose} size="3xl">
    <DialogTitle>Merge clients</DialogTitle>
    <DialogDescription>
      Choose the record the others merge into, then settle any field they disagree on. Nothing
      changes until you confirm.
    </DialogDescription>
    {props.group && (
      <MergeBody
        key={props.group.clientIds.join(',')}
        group={props.group}
        onClose={props.onClose}
        onMerged={props.onMerged}
      />
    )}
  </Dialog>
);
